import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fullDate } from '../utils/reusedfunctions';
import MachineSchematic from '../components/digital-twin/MachineSchematic';

const Icon = ({ d, size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const ListIcon      = 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01';
const CardsIcon     = 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z';
const SchematicIcon = 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8';
const ChevronIcon   = 'M6 9l6 6 6-6';

// ── Digital channel badge card ─────────────────────────────────────────────────
function DigitalCard({ ch, delay = 0 }) {
  const on = ch.value === true || ch.value === 'true' || ch.value === 1 || ch.value === '1' || String(ch.value).toLowerCase() === 'on';
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, delay }}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-hair)',
        borderRadius: 'var(--r-md)',
        padding: '12px 14px',
        display: 'flex', flexDirection: 'column', gap: 10,
        boxShadow: 'var(--sh-xs)',
      }}
    >
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 500, color: 'var(--ink-700)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {ch.name}
      </div>
      <span className={`ch-badge ${on ? 'on' : 'off'}`}>{on ? 'ON' : 'OFF'}</span>
    </motion.div>
  );
}

// ── Analog value card ──────────────────────────────────────────────────────────
function AnalogCard({ ch, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, delay }}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-hair)',
        borderRadius: 'var(--r-md)',
        padding: '14px 16px',
        boxShadow: 'var(--sh-xs)',
      }}
    >
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 500, color: 'var(--ink-700)', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {ch.name}
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 20, color: 'var(--ink-900)', letterSpacing: '-0.5px' }}>
        {ch.value !== undefined && ch.value !== null ? String(ch.value) : '—'}
      </div>
    </motion.div>
  );
}

// ── Accordion section for list view ───────────────────────────────────────────
function AccordionSection({ title, count, accentColor = 'var(--accent)', children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-hair)', borderRadius: 'var(--r-lg)', overflow: 'hidden', boxShadow: 'var(--sh-sm)', marginBottom: 12 }}>
      {/* Header */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '12px 16px',
          background: 'none', border: 'none', cursor: 'pointer',
          borderBottom: open ? '1px solid var(--border-hair)' : 'none',
          transition: 'background var(--t-fast)',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--ink-500)' }}>
            {title}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, background: 'var(--bg-raised)', color: 'var(--ink-500)', borderRadius: 10, padding: '1px 8px' }}>
            {count}
          </span>
        </div>
        <motion.div animate={{ rotate: open ? 0 : -90 }} transition={{ duration: 0.2 }}>
          <Icon d={ChevronIcon} size={14} color="var(--ink-400)" />
        </motion.div>
      </button>
      {/* Body */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── List row ──────────────────────────────────────────────────────────────────
function ListRow({ name, value, type, index }) {
  const isDigital = type === 'digital_input' || type === 'digital_output';
  const on = isDigital && (value === true || value === 'true' || value === 1 || value === '1' || String(value).toLowerCase() === 'on');
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.12, delay: index * 0.015 }}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '11px 16px',
        borderBottom: '1px solid var(--border-hair)',
        background: index % 2 === 0 ? 'var(--bg-surface)' : 'var(--bg-raised)',
      }}
    >
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-700)' }}>{name}</span>
      {isDigital
        ? <span className={`ch-badge ${on ? 'on' : 'off'}`}>{on ? 'ON' : 'OFF'}</span>
        : <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 14, color: 'var(--ink-900)' }}>
            {value !== undefined && value !== null ? String(value) : '—'}
          </span>
      }
    </motion.div>
  );
}

// ── Waiting / no-data ─────────────────────────────────────────────────────────
function WaitingState({ message }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', gap: 14 }}>
      <div style={{ width: 36, height: 36, border: '3px solid var(--border-subtle)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'atoms-spin 0.8s linear infinite' }} />
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-500)' }}>{message}</span>
    </div>
  );
}

// ── Section header (Cards view) ───────────────────────────────────────────────
function SectionHeader({ label, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, marginTop: 20 }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--ink-500)' }}>{label}</span>
      {count > 0 && <span style={{ background: 'var(--bg-inset)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)', fontSize: 10, padding: '1px 7px', borderRadius: 10 }}>{count}</span>}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
const StatusMachines = ({ machineid, statusChannels = [] }) => {
  const [statusData, setStatusData] = useState({});
  const [timeStamp,  setTimestamp]  = useState('');
  const [view,       setView]       = useState('list');
  const [isWaiting,  setIsWaiting]  = useState(true);

  useEffect(() => {
    if (!machineid) { setIsWaiting(false); return; }
    setStatusData({}); setTimestamp(''); setIsWaiting(true);

    const proto  = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const userId = localStorage.getItem('userid') || '';
    const socket = new WebSocket(`${proto}//${window.location.host}/mqttData/?machine_id=${machineid}&user_id=${userId}`);
    const timer  = setTimeout(() => setIsWaiting(false), 2500);

    socket.onopen  = () => { setTimeout(() => socket.send(JSON.stringify({ action: 'subscribe', machine_id: machineid })), 1000); };
    socket.onclose = () => setIsWaiting(false);
    socket.onerror = () => setIsWaiting(false);

    socket.onmessage = (e) => {
      const b = JSON.parse(e.data);
      const data = b.payload;
      if (!data) return;
      let cleaned = {
        analog_input:   data.analog_inputs   || [],
        analog_output:  data.analog_outputs  || [],
        digital_input:  data.digital_inputs  || [],
        digital_output: data.digital_outputs || [],
        others:         data.other           || [],
      };
      if (statusChannels?.length > 0) {
        const allowed = new Set(statusChannels);
        Object.keys(cleaned).forEach(k => { cleaned[k] = cleaned[k].filter(ch => allowed.has(ch.name)); });
      }
      setStatusData(cleaned);
      setTimestamp(data.timestamp || data.Timestamp || '');
      setIsWaiting(false);
    };

    return () => { clearTimeout(timer); socket.close(); };
  }, [machineid, statusChannels]); // eslint-disable-line

  const hasData = Object.values(statusData).some(a => Array.isArray(a) && a.length > 0);

  const allDigital = [...(statusData.digital_input || []), ...(statusData.digital_output || [])];
  const allAnalog  = [...(statusData.analog_input || []), ...(statusData.analog_output || []), ...(statusData.others || [])];

  if (isWaiting)  return <WaitingState message="Waiting for live data…" />;
  if (!hasData)   return (
    <div style={{ padding: '60px 24px', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 14, color: 'var(--ink-500)' }}>
      No live data available. The machine may be offline.
    </div>
  );

  const VIEWS = [
    { value: 'list',      icon: ListIcon,      label: 'List'      },
    { value: 'cards',     icon: CardsIcon,     label: 'Cards'     },
    { value: 'schematic', icon: SchematicIcon, label: 'Schematic' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)' }}>
          Last updated at: <span style={{ color: 'var(--ink-700)', fontWeight: 500 }}>{fullDate(timeStamp)}</span>
        </div>
        {/* View toggle */}
        <div style={{ display: 'flex', background: 'var(--bg-inset)', borderRadius: 'var(--r-md)', padding: 3, gap: 2 }}>
          {VIEWS.map(({ value, icon, label }) => (
            <button key={value} onClick={() => setView(value)} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 12px', borderRadius: 6, border: 'none',
              background: view === value ? 'var(--bg-surface)' : 'transparent',
              boxShadow: view === value ? 'var(--sh-sm)' : 'none',
              color: view === value ? 'var(--ink-900)' : 'var(--ink-500)',
              fontFamily: 'var(--font-sans)', fontSize: 12,
              fontWeight: view === value ? 500 : 400,
              cursor: 'pointer', transition: 'all var(--t-fast)',
            }}>
              <Icon d={icon} size={13} />
              {label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={view} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>

          {/* ── LIST VIEW — one accordion per payload bucket ── */}
          {view === 'list' && (
            <div>
              {[
                { key: 'digital_input',  title: 'Digital Inputs'  },
                { key: 'digital_output', title: 'Digital Outputs' },
                { key: 'analog_input',   title: 'Analog Inputs'   },
                { key: 'analog_output',  title: 'Analog Outputs'  },
                { key: 'others',         title: 'Other'           },
              ].map(({ key, title }) => {
                const channels = statusData[key] || [];
                if (!channels.length) return null;
                const isDigital = key === 'digital_input' || key === 'digital_output';
                return (
                  <AccordionSection key={key} title={title} count={channels.length} defaultOpen>
                    {channels.map((ch, i) => (
                      <ListRow key={ch.name} name={ch.name} value={ch.value} type={isDigital ? key : 'analog_input'} index={i} />
                    ))}
                  </AccordionSection>
                );
              })}
            </div>
          )}

          {/* ── CARDS VIEW ── */}
          {view === 'cards' && (
            <div>
              {allDigital.length > 0 && (
                <>
                  <SectionHeader label="Digital Channels" count={allDigital.length} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 'var(--grid-gap)', marginBottom: 8 }}>
                    {allDigital.map((ch, i) => <DigitalCard key={ch.name} ch={ch} delay={i * 0.03} />)}
                  </div>
                </>
              )}
              {allAnalog.length > 0 && (
                <>
                  <SectionHeader label="Analog & Other" count={allAnalog.length} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 'var(--grid-gap)' }}>
                    {allAnalog.map((ch, i) => <AnalogCard key={ch.name} ch={ch} delay={i * 0.03} />)}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── SCHEMATIC VIEW ── */}
          {view === 'schematic' && <MachineSchematic statusData={statusData} />}

        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default StatusMachines;
